import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { SummaryDiscountDetailsProps } from '@components/SummaryDiscountDetails/index';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { usePrices } from '@hooks/usePriceCalculator';
import { IPriceV2, IPriceV2Types, PROMO_CODE_DISCOUNT_KEYS } from '../../services';
import { getTotalPriceObjByType } from '@utils/Price.utils';
import { useJourneyType } from '@hooks/useJourneyType';

const SummaryDiscountDetailsTemplate: FC<SummaryDiscountDetailsProps> = ({ className }) => {
    const { t } = useTranslations();

    const { carDetails, currentDeal, promoCodeInDeal } = useCarDetailsDuck();

    const { formatMoney } = useMoneyFormatter();

    const { cash, finance } = usePrices();

    const { totalPrice: cashPrice } = cash;

    const { isFinance, isRent } = useJourneyType();

    const parsedPromoCode = promoCodeInDeal && JSON.parse(promoCodeInDeal);

    const [isOpen, setIsOpen] = useState(true);

    const dealerDiscount = !isRent && currentDeal?.extraFields?.agent_discount
        ? Number(JSON.parse(currentDeal?.extraFields?.agent_discount)?.amount)
        : null;

    const promoCodeDiscount = useMemo<number>(() => {
        const totalB2CPrice: IPriceV2 = getTotalPriceObjByType(
            carDetails?.pricesV2 || carDetails?.extraFields?.pricesV2,
            isFinance ? IPriceV2Types.TOTAL_B2C_FINANCE : IPriceV2Types.TOTAL_B2C_CASH
        );
        const promoCodeDiscount = totalB2CPrice?.discounts?.find(({ key }) => PROMO_CODE_DISCOUNT_KEYS.includes(key));
        return promoCodeDiscount?.absoluteAmount ?? promoCodeDiscount?.amount ?? parsedPromoCode?.discountAmount ?? 0;
    }, [carDetails, isFinance]);

    return (
        (promoCodeDiscount > 0 || dealerDiscount > 0) && (
            <div id="discount" className={className}>
                <div>
                    <Collapsible
                        lazyRender={true}
                        open={isOpen}
                        trigger={
                            <AccessibleButton
                                tabIndex={0}
                                onClick={() => setIsOpen(!isOpen)}
                                className="titleWrapperButton"
                            >
                                <div className="titleWrapper">
                                    <span className="title">{t('summary.discounts.title')}</span>
                                    <Icon
                                        className={isOpen ? 'isOpen' : 'isClose'}
                                        name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                                    />
                                </div>
                            </AccessibleButton>
                        }
                    >
                        <div className="section">
                            <p className="description">{t('summary.discounts.description')}</p>
                            <div className="discounts">
                                <div className="discount">
                                    <span className="discount_label">{t('summary.discounts.vehiclePrice.label')}</span>
                                    <span className="discount_price">
                                        {formatMoney(
                                            cashPrice + (dealerDiscount || 0) + (promoCodeDiscount || 0),
                                            true
                                        )}
                                    </span>
                                </div>
                                {!isRent && dealerDiscount > 0 && (
                                    <div className="discount">
                                        <span className="discount_label">
                                            {t('summary.discounts.dealerOffer.label')}
                                        </span>
                                        <span className="discount_price isBold">
                                            {dealerDiscount > 0 ? '-' : ''} {formatMoney(dealerDiscount || 0, true)}
                                        </span>
                                    </div>
                                )}
                                {promoCodeDiscount > 0 && (
                                    <div className="discount">
                                        <span className="discount_label">{t('summary.discounts.promoCode.label')}</span>
                                        <span className="discount_price isBold">
                                            {promoCodeDiscount > 0 ? '-' : ''} {formatMoney(promoCodeDiscount, true)}
                                        </span>
                                    </div>
                                )}
                                <div className="totalPrice">
                                    <span className="totalPrice_label">{t('summary.discounts.totalPrice.label')}</span>
                                    <span className="totalPrice_price">{formatMoney(cashPrice, true)}</span>
                                </div>
                            </div>
                        </div>
                    </Collapsible>
                </div>
            </div>
        )
    );
};

export default SummaryDiscountDetailsTemplate;
